/**
 * Campaigns domain — cache invalidation registry.
 *
 * Defines which queries every wrapped campaigns mutation should
 * invalidate on success. Consumed by `hooks.ts` via
 * `useInvalidatingMutation`. The compiler enforces that every entry in
 * `CampaignsMutationId` has a registry entry, so adding a new mutation
 * without declaring its invalidations is a build error.
 */

import type { QueryKey } from '@tanstack/react-query'

import { ALL_CAMPAIGNS_TABLE_KEY } from '../../constants/tableQueryKeys'
import type { InvalidationRule } from '../invalidation/useInvalidatingMutation'
import {
  getCampaignSettingsQueryKey,
  getCampaignScheduleQueryKey,
  getCampaignStatsQueryKey,
} from '../generated/hooks/campaignsController'

// ── Mutation identifiers ────────────────────────────────────────────

/**
 * Discriminator union of every wrapped campaigns mutation. Adding a new
 * mutation requires adding a member here and a corresponding entry in
 * `invalidationRegistry`.
 */
export type CampaignsMutationId =
  | 'updateCampaignSettings'
  | 'updateCampaignSchedule'
  | 'launchCampaign'
  | 'pauseCampaign'
  | 'resumeCampaign'
  | 'scheduleCampaignLaunch'
  | 'cancelScheduledLaunch'

// ── Shared invalidation set ─────────────────────────────────────────

/**
 * Query keys every campaign mutation should refresh (generated + legacy
 * still in use).
 */
export function commonCampaignInvalidations(): QueryKey[] {
  return [[ALL_CAMPAIGNS_TABLE_KEY], getCampaignStatsQueryKey()]
}

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Mirrors the legacy invalidateStatusCaches() set: table + stats +
 * (optional) per-campaign settings.
 */
function statusInvalidations(campaignId?: number): QueryKey[] {
  const keys = commonCampaignInvalidations()
  if (campaignId)
    keys.push(getCampaignSettingsQueryKey({ campaign_id: campaignId }))
  return keys
}

type UpdateVars = { campaign_id: number }
type StatusVars = { bison_campaign_id: number; campaign_id?: number }

// ── Registry ────────────────────────────────────────────────────────

/**
 * Typed registry mapping every campaigns mutation to the queries it
 * should invalidate on success.
 */
export const invalidationRegistry: Record<
  CampaignsMutationId,
  InvalidationRule<any>
> = {
  updateCampaignSettings: {
    invalidates: (v: UpdateVars) => [
      ...commonCampaignInvalidations(),
      getCampaignSettingsQueryKey({ campaign_id: v.campaign_id }),
    ],
  },
  updateCampaignSchedule: {
    invalidates: (v: UpdateVars) => [
      ...commonCampaignInvalidations(),
      getCampaignScheduleQueryKey({ campaign_id: v.campaign_id }),
    ],
  },
  launchCampaign: {
    invalidates: (v: StatusVars) => statusInvalidations(v.campaign_id),
  },
  pauseCampaign: {
    invalidates: (v: StatusVars) => statusInvalidations(v.campaign_id),
  },
  resumeCampaign: {
    invalidates: (v: StatusVars) => statusInvalidations(v.campaign_id),
  },
  scheduleCampaignLaunch: {
    invalidates: (v: StatusVars) => statusInvalidations(v.campaign_id),
  },
  cancelScheduledLaunch: {
    invalidates: (v: StatusVars) => statusInvalidations(v.campaign_id),
  },
}
